import { inject, provide, ref, watch, type ComputedRef, type InjectionKey } from 'vue';
import type { AccountConfigHandle } from '../config/accountConfig';
import { dictionaryOwnDocument } from '../config/dictionaryLayer';
import {
  addSpellDictionaryWord,
  spellCorrectionPatch,
  spellCorrectionsLocked,
  spellNeverSuggestPatch,
  useSpellCheckConfig,
  type SpellCheckConfig,
} from '../config/spellCheck';
import {
  searchLearnCorrections,
  useSearchSynonymsConfig,
  type SearchSynonymsConfig,
} from '../config/searchSynonyms';
import { dictionaryWord, type DictionaryLanguage } from '../text/dictionaryFile';
import { dictionaryWriteFailure } from '../text/dictionary/level';
import { resolvedDictionaryFolds } from './dictionaryFolds';
import { dictionaryFold } from '../text/dictionaryFold';
import { learnedSpellCorrection } from '../text/dictionaryLearn';

/**
 * What a „Meinten Sie“ row can do with a word (I-3024).
 *
 * {@link createMasterDictionaryActions} is the shell's instance, all of it on
 * the account config path (the one settings system, I-3024): a confirmed word
 * is merged into `spell_check.personal_dictionary`, a hidden word into
 * `spell_check.never_suggest` (a refused write rejects) and an accepted
 * suggestion into `spell_check.corrections`, counted up to five.
 * {@link createSessionDictionaryActions} is the stand-in for a mount that has
 * no session: it remembers the words and sends no request.
 */
export interface SearchDictionaryActions {
  /**
   * „„getippt“ ist richtig“ (the row's `confirm`): the typed `word` is a word of
   * the user's own dictionary and is never corrected again.
   */
  addToDictionary(word: string): Promise<void>;
  /**
   * „„X“ nicht mehr vorschlagen“: never offer the suggested `text` again
   * (`lang` is the word's language, default German). Rejects when the write
   * was refused (the master instance); the caller reports that as a write
   * failure and keeps its row so the user can retry.
   */
  neverSuggest(text: string, lang?: DictionaryLanguage): Promise<void>;
  /**
   * The user took the suggestion: `accepted` replaces the typed word.
   * A master instance learns the correction when the setting allows it and
   * the corrections are not locked; it never fails.
   */
  learn(typed: string, accepted: string, lang?: DictionaryLanguage): Promise<void>;
  /** The typed word is in the user's dictionary: do not ask for a correction. */
  isOwnWord(word: string): boolean;
  /** The suggestion was blocked with `neverSuggest`: drop it. */
  isBlocked(text: string): boolean;
  /**
   * Runs after a word is added or hidden, or a correction was learned. The
   * search-dictionary filter cache refreshes from this, so a word hidden here
   * leaves every module's suggestions.
   * Returns an unsubscribe.
   */
  subscribe(listener: () => void): () => void;
}

export const searchDictionaryActionsKey: InjectionKey<SearchDictionaryActions> = Symbol(
  'searchDictionaryActions',
);

/**
 * Share one dictionary with the shell and every app. Call it from the shell's
 * setup (`App.vue`); a hosted app reads it with {@link useSearchDictionaryActions}.
 */
export function provideSearchDictionaryActions(actions: SearchDictionaryActions): void {
  provide(searchDictionaryActionsKey, actions);
}

/**
 * The dictionary the shell provided. With no provider — an ERP test, a
 * standalone mount — each call gets its own session instance, so nothing is
 * shared by accident and the caller still has somewhere to remember a word.
 */
export function useSearchDictionaryActions(): SearchDictionaryActions {
  return inject(searchDictionaryActionsKey, createSessionDictionaryActions, true);
}

function rememberedWord(word: string): string {
  return word.trim().toLowerCase();
}

/**
 * Interim dictionary: two in-memory sets of lower-case words, the user's own
 * words and the blocked suggestions. `isOwnWord` and `isBlocked` read a
 * revision, so a Vue reader re-evaluates when a word is added and a shown
 * suggestion can drop. Nothing here sends a request. `learn` remembers nothing:
 * a session mount has no account to store the correction on.
 */
export function createSessionDictionaryActions(): SearchDictionaryActions {
  const ownWords = new Set<string>();
  const blocked = new Set<string>();
  const revision = ref(0);
  const listeners = new Set<() => void>();
  const notify = (): void => {
    for (const listener of listeners) listener();
  };
  const rememberIn = (set: Set<string>) => (word: string): Promise<void> => {
    const key = rememberedWord(word);
    if (key && !set.has(key)) {
      set.add(key);
      revision.value += 1;
      notify();
    }
    return Promise.resolve();
  };
  const hasIn = (set: Set<string>) => (word: string): boolean => {
    // Read during a computed or watch so that reader runs again after a change.
    void revision.value;
    const key = rememberedWord(word);
    return key !== '' && set.has(key);
  };
  return {
    addToDictionary: rememberIn(ownWords),
    neverSuggest: rememberIn(blocked),
    async learn() {},
    isOwnWord: hasIn(ownWords),
    isBlocked: hasIn(blocked),
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** What the master dictionary reads and writes; each defaults to the shared handle of its key. */
export interface MasterDictionaryHandles {
  /** `spell_check` with its layers (the own layer is read before a correction is learned). */
  spell?: AccountConfigHandle<SpellCheckConfig>;
  /** `search`, for `learnCorrections`. */
  search?: AccountConfigHandle<SearchSynonymsConfig>;
}

/**
 * The shell's dictionary on the account config path. `addToDictionary`
 * merge-patches `{ [word]: true }` into `spell_check.personal_dictionary` on
 * the session's own layer (`addSpellDictionaryWord`). `neverSuggest` merges
 * the word into `spell_check.never_suggest` and rejects when that write is
 * refused. `learn` records an accepted „Meinten Sie“ in
 * `spell_check.corrections` of the own layer: it reads that layer, writes
 * nothing when `search.learnCorrections` is off or the corrections are
 * locked, nor for an entry that is saturated (count 5), typed in or imported,
 * and otherwise writes `{ to, count, origin: "learned" }` under the key the own layer already
 * holds for the word (found by fold), once `search` has loaded — on a refusal
 * it reads the layer once more and decides again, with no retry of its own. A failed
 * `addToDictionary` or `learn` resolves and leaves the row usable.
 * `isOwnWord` reads the effective personal dictionary and the words added in
 * this tab, `isBlocked` the effective never-suggest words and the ones
 * hidden in this tab. A word of this tab that the settings held and no longer
 * hold (removed on the „Wörterbuch“ page) is forgotten at once.
 *
 * Call it from a component setup.
 */
export function createMasterDictionaryActions(handles: MasterDictionaryHandles = {}): SearchDictionaryActions {
  const handle = handles.spell ?? useSpellCheckConfig({ layers: true });
  const search = handles.search ?? useSearchSynonymsConfig();
  const folds = resolvedDictionaryFolds(handle);
  const own = new Set<string>();
  const blocked = new Set<string>();
  // Session words the resolved settings have held at least once: when they
  // stop holding one (removed on the dictionary page), the session word goes.
  const ownSeen = new Set<string>();
  const blockedSeen = new Set<string>();
  const revision = ref(0);
  const listeners = new Set<() => void>();
  const notify = (): void => {
    for (const listener of listeners) listener();
  };
  const bump = (set: Set<string>, seen: Set<string>, settings: ComputedRef<ReadonlySet<string>>, key: string): void => {
    if (!key || set.has(key)) return;
    set.add(key);
    if (settings.value.has(dictionaryFold(key))) seen.add(key);
    revision.value += 1;
    notify();
  };
  // „Allow again“ / removing a word on the „Wörterbuch“ page takes effect
  // here without a reload: a word the session added and the settings held
  // before is dropped from the session once the settings lose it. A word the
  // settings never held (the write is still on its way) stays.
  const forgetRemoved = (set: Set<string>, seen: Set<string>, settings: ComputedRef<ReadonlySet<string>>): void => {
    watch(
      settings,
      (resolved) => {
        let changed = false;
        for (const key of [...set]) {
          if (resolved.has(dictionaryFold(key))) {
            seen.add(key);
          } else if (seen.has(key)) {
            set.delete(key);
            seen.delete(key);
            changed = true;
          }
        }
        if (!changed) return;
        revision.value += 1;
        notify();
      },
      { flush: 'sync' },
    );
  };
  forgetRemoved(own, ownSeen, folds.personal);
  forgetRemoved(blocked, blockedSeen, folds.never);

  /**
   * Learns once. The write is a write of its own (`retryConflict: false`): it
   * never joins a queued batch, a refused one never takes a word addition
   * down with it, a conflict is not retried with the patch as decided, and a
   * failure stays out of the handle's `error` — a silent learn failure stays
   * silent. It carries the row version the decision was made on (`ifMatch`),
   * so a write that waited behind another one of this tab is refused when that
   * one changed the row. On a refusal the layer is read again and the rule is
   * applied once more.
   */
  async function learnOnce(typed: string, accepted: string, lang: DictionaryLanguage, again: boolean): Promise<void> {
    // `learnCorrections` defaults to on: wait until the key is loaded, or a
    // user who switched it off would be learned from while it loads.
    if (!search.loaded.value) {
      await search.reload();
      if (!search.loaded.value) return;
    }
    if (!searchLearnCorrections(search.effective.value)) return;
    if (spellCorrectionsLocked(handle.enforced.value)) return;
    if (handle.layers.value === null) {
      await handle.reload();
      if (handle.layers.value === null) return;
    }
    const learned = learnedSpellCorrection(
      dictionaryOwnDocument(handle.layers.value, handle.target.value),
      lang,
      typed,
      accepted,
    );
    if (!learned) return;
    // The version the decision was made on: the write may wait behind others
    // (a manual edit of the same word), and then master must refuse it.
    const decidedOn = handle.version.value;
    const written = await handle.patch(spellCorrectionPatch(lang, learned.typed, learned.correction), {
      immediate: true,
      retryConflict: false,
      ifMatch: decidedOn,
    });
    if (written !== null) {
      // A learned correction changes what the modules answer: readers drop what they kept.
      notify();
      return;
    }
    if (again) {
      await handle.reload();
      await learnOnce(typed, accepted, lang, false);
    }
  }

  return {
    async addToDictionary(word) {
      let result: Awaited<ReturnType<typeof addSpellDictionaryWord>>;
      try {
        result = await addSpellDictionaryWord(handle, word);
      } catch {
        return;
      }
      if (result === 'added' || result === 'known') bump(own, ownSeen, folds.personal, rememberedWord(word));
    },
    async neverSuggest(text, lang = 'de') {
      const patch = spellNeverSuggestPatch(lang, text);
      if (!patch) return;
      // A refused write rejects: the caller reports it and keeps its row. The word stays unblocked.
      const written = await handle.patch(patch, { immediate: true });
      if (written === null) {
        const cause = handle.error.value;
        throw dictionaryWriteFailure({
          ok: false,
          status: cause?.status ?? 0,
          code: cause?.code ?? '',
          message: cause?.message ?? '',
        });
      }
      bump(blocked, blockedSeen, folds.never, rememberedWord(text));
    },
    async learn(typed, accepted, lang = 'de') {
      try {
        await learnOnce(typed, accepted, lang, true);
      } catch {
        // The field already shows the accepted word.
      }
    },
    isOwnWord(word) {
      void revision.value;
      const key = rememberedWord(word);
      if (!key) return false;
      return own.has(key) || folds.personal.value.has(dictionaryFold(word));
    },
    isBlocked(text) {
      void revision.value;
      const key = rememberedWord(text);
      if (!key) return false;
      return blocked.has(key) || folds.never.value.has(dictionaryFold(text));
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
